import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, FileText, PenLine, Search } from 'lucide-react';
import { EmptyState, ErrorState, Skeleton, StatusBadge, SubBar } from '@/design/components';
import { formatDate } from '@/lib/dates';
import {
  groupByCategory,
  searchDocuments,
  useDocuments,
  usePendingSignatures,
  type Document,
} from './api';

/**
 * מאגר הנהלים, מחולק לקטגוריות ועם חיפוש (PRD 5.10.1).
 *
 * החנות, המחסן והמשרד הם מקומות עבודה שונים, ועובד מחפש נוהל לפי
 * המקום שבו הוא עומד. לכן זו אינה רשימה אחת ארוכה אלא בלוק לכל
 * קטגוריה, בסדר שנקבע במסך הניהול.
 *
 * בחיפוש החלוקה נעלמת: מי שמחפש כבר יודע מה הוא רוצה, והבלוקים
 * רק היו פורסים תוצאה אחת על כמה כותרות.
 */
export function DocumentsPage() {
  const documents = useDocuments();
  const pending = usePendingSignatures();
  const [term, setTerm] = useState('');

  if (documents.isPending) return <Skeleton className="m-4 h-40" />;
  if (documents.isError) return <ErrorState onRetry={() => void documents.refetch()} />;

  const all = documents.data ?? [];
  const searching = term.trim() !== '';
  const found = searchDocuments(all, term);
  const groups = searching ? [] : groupByCategory(all);
  const waiting = new Set((pending.data ?? []).map((item) => item.document_id));

  return (
    <div className="grid content-start gap-4">
      <SubBar title="נהלים" subtitle="איך עובדים אצלנו" />

      <div className="grid gap-4 px-4 pb-8">
        {waiting.size > 0 && (
          <p className="m-0 rounded-md border border-warn bg-raised p-3 text-body-sm">
            {waiting.size === 1
              ? 'יש נוהל אחד שמחכה לחתימה שלך.'
              : `יש ${waiting.size} נהלים שמחכים לחתימה שלך.`}
          </p>
        )}

        {all.length > 0 && (
          <div className="relative">
            <Search
              size={20}
              strokeWidth={1.75}
              aria-hidden="true"
              className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted"
            />
            <input
              type="search"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              aria-label="חיפוש בנהלים"
              placeholder="שם הנוהל, קטגוריה או תוכן"
              className="min-h-11 w-full rounded-sm border border-muted bg-surface ps-10 pe-3 text-body text-ink"
            />
          </div>
        )}

        {all.length === 0 && (
          <EmptyState title="אין עדיין נהלים" description="כשההנהלה תפרסם נוהל הוא יופיע כאן." />
        )}

        {searching && found.length === 0 && (
          <EmptyState title="לא נמצא נוהל" description={`אין תוצאה ל"${term}".`} />
        )}

        {searching && found.length > 0 && (
          <DocumentList documents={found} waiting={waiting} showCategories />
        )}

        {!searching &&
          groups.map((group) => (
            <section key={group.id ?? 'loose'} className="grid gap-2">
              <h2 className="m-0 text-title font-semibold text-ink">
                {group.name}{' '}
                <span className="font-normal text-muted">({group.documents.length})</span>
              </h2>
              <DocumentList documents={group.documents} waiting={waiting} />
            </section>
          ))}
      </div>
    </div>
  );
}

/**
 * showCategories נדרש בחיפוש בלבד: שם אין כותרת בלוק שתאמר לאיזו
 * קטגוריה הנוהל שייך, ובלעדיה התוצאה מגיעה בלי הקשר.
 */
function DocumentList({
  documents,
  waiting,
  showCategories = false,
}: {
  documents: Document[];
  waiting: Set<string>;
  showCategories?: boolean;
}) {
  return (
    <ul className="m-0 grid list-none gap-2 p-0">
      {documents.map((document) => (
        <li key={document.id}>
          <Link
            to={`/documents/${document.id}`}
            className="flex items-center gap-3 rounded-md border border-line bg-raised p-3 no-underline"
          >
            <FileText
              size={20}
              strokeWidth={1.75}
              aria-hidden="true"
              className="shrink-0 text-muted"
            />
            <span className="grid min-w-0 grow gap-0.5">
              <span className="truncate text-title font-medium text-ink">{document.title}</span>
              <span className="text-label text-muted">
                {showCategories &&
                  document.categories.length > 0 &&
                  `${document.categories.map((category) => category.name).join(' · ')} · `}
                {document.version > 1 && `גרסה ${document.version} · `}
                {`עודכן ${formatDate(document.updated_at)}`}
              </span>
            </span>

            {waiting.has(document.id) && (
              <StatusBadge tone="warn" icon={PenLine}>
                לחתימה
              </StatusBadge>
            )}

            <ChevronLeft
              size={20}
              strokeWidth={1.75}
              aria-hidden="true"
              className="shrink-0 text-muted"
            />
          </Link>
        </li>
      ))}
    </ul>
  );
}
